/**
 * The fixed 1-5 slot palette every side-aware surface draws from — the rail, the
 * donut, and the board rings. A side's colour is a property of its SLOT, never
 * picked from its own data, so it never changes mid-fight and never disagrees
 * between two surfaces drawing the same side.
 *
 * The CSS variable carries a fallback for the one context that cannot read the
 * cascade — a canvas 2D context. Keep these byte-identical to `--pl-side-1..5`
 * in `assets/styles/player-theme.css`, which documents why these particular
 * hues: they have to stay clear of the board's relation palette, because a
 * token carries its relation in its body and its side in the ring around it.
 */
const SIDE_COLOR_FALLBACKS = ['#3ecfe0', '#b98cf5', '#e8749f', '#5b9ef5', '#c98b5a'];

/*
 * The dimmed set, for surfaces that colour a large area by side rather than
 * drawing a thin mark on one — the establishment screen's wedges and washes.
 * Same slot, same hue order, environmental rather than neon. Keep these
 * byte-identical to `--pl-side-quiet-1..5` in `player-theme.css`.
 */
const SIDE_QUIET_FALLBACKS = ['#4d6f78', '#6b6383', '#855a66', '#4f6478', '#856544'];

export function sideQuietColorVar(slot) {
  const index = ((slot ?? 1) - 1 + SIDE_QUIET_FALLBACKS.length) % SIDE_QUIET_FALLBACKS.length;
  return `var(--pl-side-quiet-${slot}, ${SIDE_QUIET_FALLBACKS[index]})`;
}

export function sideColorFallback(slot) {
  return SIDE_COLOR_FALLBACKS[((slot ?? 1) - 1 + SIDE_COLOR_FALLBACKS.length) % SIDE_COLOR_FALLBACKS.length];
}

/** A CSS colour value — a `var(--pl-side-N, fallback)` reference — for style bindings. */
export function sideColorVar(slot) {
  return `var(--pl-side-${slot}, ${sideColorFallback(slot)})`;
}

/**
 * A resolved colour string, for contexts that cannot use `var()` — a canvas 2D
 * context does not participate in the CSS cascade, so `ctx.strokeStyle = 'var(...)'`
 * is silently ignored. Reads the variable off `element`'s computed style so a themed
 * value still wins there once Task 17 lands; falls back to the same palette otherwise.
 */
export function resolveSideColor(slot, element) {
  if (typeof window === 'undefined' || !element || element.nodeType !== 1) {
    return sideColorFallback(slot);
  }
  const value = window.getComputedStyle(element).getPropertyValue(`--pl-side-${slot}`).trim();
  return value || sideColorFallback(slot);
}
